import { Fragment, useState } from 'react'
import { cn } from '../lib/cn'

/**
 * Diferença entre dois textos, linha a linha. Sem verde e sem vermelho: o que saiu fica riscado em
 * cinza; o que entrou fica em ink, com fio embaixo. A densidade de tinta diz o que mudou.
 * Lado a lado a partir de `desk`; em sequência no celular (ou com `layout="unified"`).
 * Trechos iguais longos se recolhem e abrem ao toque.
 */

export interface DiffViewPart {
  text: string
  /** A parte que difere do outro lado. */
  changed?: boolean
}

export interface DiffViewSide {
  line: number
  parts: DiffViewPart[]
}

export interface DiffViewRow {
  type: 'equal' | 'added' | 'removed' | 'changed'
  before: DiffViewSide | null
  after: DiffViewSide | null
}

export interface DiffViewProps {
  rows: DiffViewRow[]
  /** Rótulo de cada lado ("v3 · 2026.08", "Rascunho atual"). */
  beforeLabel: string
  afterLabel: string
  layout?: 'split' | 'unified'
  /** Linhas iguais mantidas em volta de cada mudança; o resto se recolhe. Padrão 3. */
  context?: number
  /** Texto do trecho recolhido. */
  collapsedLabel?: (count: number) => string
  className?: string
}

type Chunk =
  { kind: 'rows'; rows: DiffViewRow[] } | { kind: 'collapsed'; rows: DiffViewRow[]; id: number }

function chunkRows(rows: DiffViewRow[], context: number): Chunk[] {
  const keep = new Array<boolean>(rows.length).fill(false)
  rows.forEach((row, index) => {
    if (row.type === 'equal') return
    for (
      let i = Math.max(0, index - context);
      i <= Math.min(rows.length - 1, index + context);
      i++
    ) {
      keep[i] = true
    }
  })
  const chunks: Chunk[] = []
  let id = 0
  let index = 0
  while (index < rows.length) {
    const start = index
    const visible = keep[index]!
    while (index < rows.length && keep[index] === visible) index++
    const slice = rows.slice(start, index)
    // Recolher duas linhas não poupa nada a quem lê.
    if (visible || slice.length < 3) {
      const last = chunks[chunks.length - 1]
      if (last?.kind === 'rows') last.rows.push(...slice)
      else chunks.push({ kind: 'rows', rows: slice })
    } else chunks.push({ kind: 'collapsed', rows: slice, id: id++ })
  }
  return chunks
}

function Parts({ side, tone }: { side: DiffViewSide; tone: 'before' | 'after' | 'plain' }) {
  return (
    <>
      {side.parts.map((part, index) =>
        part.changed ? (
          tone === 'before' ? (
            <del key={index} className="text-label decoration-label line-through">
              {part.text}
            </del>
          ) : (
            <ins key={index} className="border-ink text-ink border-b font-normal no-underline">
              {part.text}
            </ins>
          )
        ) : (
          <Fragment key={index}>{part.text}</Fragment>
        ),
      )}
    </>
  )
}

function Cell({
  side,
  tone,
  marked,
}: {
  side: DiffViewSide | null
  tone: 'before' | 'after'
  marked: boolean
}) {
  return (
    <div
      data-slot="diff-cell"
      data-side={tone}
      className={cn(
        'flex min-w-0 gap-3 border-l-2 px-3 py-[3px]',
        !side && 'bg-pampas border-transparent',
        side && !marked && 'border-transparent',
        side && marked && tone === 'before' && 'border-hairline-heavy bg-pampas',
        side && marked && tone === 'after' && 'border-ink bg-pampas',
      )}
    >
      <span
        aria-hidden
        className="text-label-soft w-8 shrink-0 text-right text-[11px] leading-[1.7] tabular-nums select-none"
      >
        {side ? side.line : ''}
      </span>
      <span aria-hidden className="text-label w-3 shrink-0 text-[11px] leading-[1.7] select-none">
        {side && marked ? (tone === 'before' ? '−' : '+') : ''}
      </span>
      <span className="min-w-0 flex-1 break-words whitespace-pre-wrap">
        {side ? <Parts side={side} tone={marked ? tone : 'plain'} /> : null}
      </span>
    </div>
  )
}

export function DiffView({
  rows,
  beforeLabel,
  afterLabel,
  layout = 'split',
  context = 3,
  collapsedLabel = (count) => `${count} linhas iguais`,
  className,
}: DiffViewProps) {
  const [expanded, setExpanded] = useState<ReadonlySet<number>>(new Set())
  const chunks = chunkRows(rows, context)
  const split = layout === 'split'

  const renderRow = (row: DiffViewRow, key: string) => {
    const marked = row.type !== 'equal'
    if (!split) {
      return (
        <Fragment key={key}>
          {row.type === 'equal' ? (
            <Cell side={row.after} tone="after" marked={false} />
          ) : (
            <>
              {row.before ? <Cell side={row.before} tone="before" marked /> : null}
              {row.after ? <Cell side={row.after} tone="after" marked /> : null}
            </>
          )}
        </Fragment>
      )
    }
    return (
      <div key={key} className="desk:grid desk:grid-cols-2">
        <div className={cn(!row.before && 'max-desk:hidden')}>
          <Cell side={row.before} tone="before" marked={marked} />
        </div>
        <div className={cn('desk:border-hairline desk:border-l', !row.after && 'max-desk:hidden')}>
          <Cell side={row.after} tone="after" marked={marked} />
        </div>
      </div>
    )
  }

  return (
    <div
      data-slot="diff-view"
      data-layout={layout}
      className={cn(
        'border-hairline bg-surface font-body text-ink border text-[13px] leading-[1.7] font-light',
        className,
      )}
    >
      <div
        className={cn(
          'border-hairline bg-pampas eyebrow border-b',
          split ? 'desk:grid desk:grid-cols-2' : 'flex flex-wrap gap-x-6',
        )}
      >
        <div className="px-4 py-3">{beforeLabel}</div>
        <div className={cn('px-4 py-3', split && 'desk:border-hairline desk:border-l')}>
          {afterLabel}
        </div>
      </div>
      {chunks.map((chunk, chunkIndex) =>
        chunk.kind === 'collapsed' && !expanded.has(chunk.id) ? (
          <button
            key={`c${chunk.id}`}
            type="button"
            onClick={() => setExpanded((current) => new Set(current).add(chunk.id))}
            className="border-hairline text-label hover:text-ink hover:bg-pampas block w-full cursor-pointer border-y px-4 py-2 text-left text-[11px] leading-[1.6]"
          >
            {collapsedLabel(chunk.rows.length)} · mostrar
          </button>
        ) : (
          chunk.rows.map((row, rowIndex) => renderRow(row, `${chunkIndex}-${rowIndex}`))
        ),
      )}
    </div>
  )
}
